import { listModules } from "../modules";
import { getSprite, type SpriteSource } from "../../sprites";

/** A single OBB-rect inside a sprite's footprint, in *cells* relative
 *  to the sprite's natural-orientation center. The SubCollider builder
 *  rotates these by the module's facing. */
interface CollisionRect {
  cx: number;
  cy: number;
  /** Half-extents in cells. */
  hw: number;
  hh: number;
}

/** Raw alpha mask kept around per-sprite so the SDF builder can blit it
 *  into ship-level bitmaps without re-rasterizing the SVG. One byte per
 *  art-pixel: 0 = transparent, 1 = opaque (after threshold). */
interface SpriteMask {
  data: Uint8Array;
  w: number;
  h: number;
}

const ART_PX_PER_CELL = 8;

/** Oversample factor before decoding the alpha mask. >1 makes the mask
 *  robust against SVG-decoder subpixel antialiasing; we max-pool back
 *  down so any opaque subpixel marks the whole art-pixel opaque. */
const RASTER_SCALE = 2;
const ALPHA_THRESHOLD = 96;
const MAX_RECTS_PER_SPRITE = 5;
const MIN_COVERAGE = 0.96;
const MIN_RECT_PIXELS = 2;

const SHAPES = new Map<string, CollisionRect[]>();
const MASKS = new Map<string, SpriteMask>();

/** Returns null when the sprite's footprint is fully opaque — caller
 *  should fall back to a single full-cell SubCollider. */
export function getCollisionShape(spriteKey: string): CollisionRect[] | null {
  return SHAPES.get(spriteKey) ?? null;
}

/** Returns null for fully-opaque sprites (their per-pixel mask is just
 *  all-1s; the SDF builder treats this as a fast solid-rect blit). */
export function getSpriteMask(spriteKey: string): SpriteMask | null {
  return MASKS.get(spriteKey) ?? null;
}

export async function buildCollisionShapes(): Promise<void> {
  SHAPES.clear();
  MASKS.clear();
  const seen = new Set<string>();
  await Promise.all(
    listModules().map(async (def) => {
      if (seen.has(def.sprite)) return;
      seen.add(def.sprite);
      const src = getSprite(def.sprite);
      const mask = await rasterizeMask(src);
      const rects = decompose(mask.data, mask.w, mask.h);
      if (rects.length === 0) return;
      // Fully opaque sprites take the SDF builder's fast solid-rect path
      // and don't need the per-pixel data cached.
      if (!isTrivialFullBBox(rects, mask.w, mask.h)) {
        MASKS.set(def.sprite, mask);
        SHAPES.set(def.sprite, toCellRects(rects, src.width, src.height));
      }
    }),
  );
}

interface PixelRect {
  x: number;
  y: number;
  w: number;
  h: number;
}

async function rasterizeMask(src: SpriteSource): Promise<SpriteMask> {
  const w = src.width;
  const h = src.height;
  const rw = w * RASTER_SCALE;
  const rh = h * RASTER_SCALE;
  const blob = new Blob([src.svg], { type: "image/svg+xml" });
  let bmp: ImageBitmap;
  try {
    bmp = await createImageBitmap(blob, {
      resizeWidth: rw,
      resizeHeight: rh,
      resizeQuality: "pixelated",
    });
  } catch {
    const url = URL.createObjectURL(blob);
    try {
      const img = new Image();
      img.src = url;
      await img.decode();
      const cv = new OffscreenCanvas(rw, rh);
      const cx = cv.getContext("2d");
      if (!cx) throw new Error("no 2d context for SVG fallback");
      cx.imageSmoothingEnabled = false;
      cx.drawImage(img, 0, 0, rw, rh);
      bmp = cv.transferToImageBitmap();
    } finally {
      URL.revokeObjectURL(url);
    }
  }
  const cv = new OffscreenCanvas(rw, rh);
  const ctx = cv.getContext("2d", { alpha: true });
  if (!ctx) throw new Error("could not get OffscreenCanvas 2d context");
  ctx.imageSmoothingEnabled = false;
  ctx.clearRect(0, 0, rw, rh);
  ctx.drawImage(bmp, 0, 0);
  const px = ctx.getImageData(0, 0, rw, rh).data;

  const mask = new Uint8Array(w * h);
  for (let y = 0; y < h; y++) {
    for (let x = 0; x < w; x++) {
      let opaque = 0;
      for (let dy = 0; dy < RASTER_SCALE && !opaque; dy++) {
        for (let dx = 0; dx < RASTER_SCALE && !opaque; dx++) {
          const sx = x * RASTER_SCALE + dx;
          const sy = y * RASTER_SCALE + dy;
          if (px[(sy * rw + sx) * 4 + 3] >= ALPHA_THRESHOLD) opaque = 1;
        }
      }
      mask[y * w + x] = opaque;
    }
  }
  return { data: mask, w, h };
}

function decompose(mask: Uint8Array, W: number, H: number): PixelRect[] {
  const work = new Uint8Array(mask);
  let totalOpaque = 0;
  for (let i = 0; i < work.length; i++) if (work[i]) totalOpaque++;
  if (totalOpaque === 0) return [];

  const rects: PixelRect[] = [];
  let covered = 0;
  const target = totalOpaque * MIN_COVERAGE;
  while (rects.length < MAX_RECTS_PER_SPRITE && covered < target) {
    const r = largestRect(work, W, H);
    if (!r || r.w * r.h < MIN_RECT_PIXELS) break;
    rects.push(r);
    for (let y = r.y; y < r.y + r.h; y++) {
      for (let x = r.x; x < r.x + r.w; x++) {
        work[y * W + x] = 0;
      }
    }
    covered += r.w * r.h;
  }
  return rects;
}

function largestRect(mask: Uint8Array, W: number, H: number): PixelRect | null {
  const height = new Int32Array(W);
  const stack: number[] = [];
  let bestArea = 0;
  let best: PixelRect | null = null;

  for (let r = 0; r < H; r++) {
    for (let c = 0; c < W; c++) {
      height[c] = mask[r * W + c] ? height[c] + 1 : 0;
    }
    stack.length = 0;
    for (let c = 0; c <= W; c++) {
      const h = c === W ? 0 : height[c];
      while (stack.length > 0 && height[stack[stack.length - 1]] > h) {
        const top = stack.pop()!;
        const left = stack.length === 0 ? -1 : stack[stack.length - 1];
        const width = c - left - 1;
        const hh = height[top];
        const area = hh * width;
        if (area > bestArea) {
          bestArea = area;
          best = { x: left + 1, y: r - hh + 1, w: width, h: hh };
        }
      }
      stack.push(c);
    }
  }
  return best;
}

function isTrivialFullBBox(rects: PixelRect[], W: number, H: number): boolean {
  if (rects.length !== 1) return false;
  const r = rects[0];
  return r.x === 0 && r.y === 0 && r.w === W && r.h === H;
}

function toCellRects(rects: PixelRect[], W: number, H: number): CollisionRect[] {
  const halfW = W / 2;
  const halfH = H / 2;
  return rects.map((r) => ({
    cx: (r.x + r.w / 2 - halfW) / ART_PX_PER_CELL,
    cy: (r.y + r.h / 2 - halfH) / ART_PX_PER_CELL,
    hw: (r.w / 2) / ART_PX_PER_CELL,
    hh: (r.h / 2) / ART_PX_PER_CELL,
  }));
}
